import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, Empty, Form, Input, InputNumber, Modal, Popconfirm, Row, Segmented,
  Select, Space, Spin, Table, Tabs, Tag, Tooltip, Typography, message,
} from 'antd';
import {
  ArrowLeftOutlined, CheckCircleOutlined, DeleteOutlined, DollarOutlined, DownloadOutlined, EditOutlined,
  MoneyCollectOutlined, PlusOutlined, SyncOutlined, TeamOutlined, WalletOutlined, YoutubeOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { PageHero, StatTile } from '../components/ui';

const { Text } = Typography;

const usd = (v) => `$${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inr = (v) => (v === null || v === undefined ? '—' : `₹${Math.round(Number(v)).toLocaleString('en-IN')}`);
const monthLabel = (m) => (m ? new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'long', year: 'numeric' }) : '');
const today = () => new Date().toISOString().slice(0, 10);
const errText = (e) => e?.response?.data?.error || e.message || 'Failed';

function ClientForm({ open, client, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) form.setFieldsValue(client || { name: '', phone: '', email: '', notes: '', default_cut: 0 });
  }, [open, client, form]);
  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      if (client) await api.put(`/client-accounts/${client.id}`, v);
      else await api.post('/client-accounts', v);
      message.success(client ? 'Client updated' : 'Client created');
      onSaved();
    } catch (e) { message.error(errText(e)); } finally { setSaving(false); }
  };
  return (
    <Modal open={open} title={client ? 'Edit client' : 'New client'} onCancel={onClose} onOk={save} confirmLoading={saving} okText="Save" destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Client name" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="default_cut" label="Default revenue cut % (our share; new channels start with this)"><InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" /></Form.Item>
        <Row gutter={12}>
          <Col xs={24} sm={12}><Form.Item name="phone" label="Phone"><Input /></Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="email" label="Email"><Input /></Form.Item></Col>
        </Row>
        <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
      </Form>
    </Modal>
  );
}

function ChannelForm({ open, clientId, channel, defaultCut, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [cmsChannels, setCmsChannels] = useState([]);
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue(channel || { channel_id: undefined, channel_name: '', cut_percent: defaultCut, linked_date: '', unlinked_date: '', vendor: '', cms_company: '', notes: '' });
    api.get('/client-accounts/cms-channels').then((r) => setCmsChannels(r.data.channels || [])).catch(() => {});
  }, [open, channel, defaultCut, form]);
  const options = cmsChannels.map((c) => ({
    value: c.channel_id,
    label: `${c.channel_name || c.channel_id}${c.client_name ? ` — linked to ${c.client_name}` : ''}`,
    disabled: !!c.client_name && c.channel_id !== channel?.channel_id,
  }));
  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      if (channel) await api.put(`/client-accounts/channels/${channel.id}`, v);
      else await api.post(`/client-accounts/${clientId}/channels`, v);
      message.success('Channel saved');
      onSaved();
    } catch (e) { message.error(errText(e)); } finally { setSaving(false); }
  };
  return (
    <Modal open={open} title={channel ? 'Edit channel' : 'Add channel'} onCancel={onClose} onOk={save} confirmLoading={saving} okText="Save" destroyOnClose>
      <Form form={form} layout="vertical">
        <Form.Item name="channel_id" label="Channel (from CMS) — or paste channel ID / link" rules={[{ required: true }]}>
          <Select showSearch allowClear options={options} optionFilterProp="label" placeholder="Search channel"
            onSearch={(text) => { if (/UC[A-Za-z0-9_-]{22}/.test(text)) form.setFieldsValue({ channel_id: text.match(/UC[A-Za-z0-9_-]{22}/)[0] }); }}
            onChange={(id) => { const c = cmsChannels.find((x) => x.channel_id === id); if (c && !form.getFieldValue('channel_name')) form.setFieldsValue({ channel_name: c.channel_name }); }} />
        </Form.Item>
        <Form.Item name="channel_name" label="Channel name"><Input /></Form.Item>
        <Form.Item name="cut_percent" label="Revenue cut % (our share)" extra="Example: 30 means we keep 30%, client gets 70%."><InputNumber min={0} max={100} style={{ width: '100%' }} addonAfter="%" /></Form.Item>
        <Row gutter={12}>
          <Col xs={24} sm={12}><Form.Item name="linked_date" label="Linked date" extra="Revenue counts from this day"><Input type="date" /></Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="unlinked_date" label="Unlinked date" extra="Revenue counts up to this day"><Input type="date" /></Form.Item></Col>
        </Row>
        <Row gutter={12}>
          <Col xs={24} sm={12}><Form.Item name="vendor" label="Vendor"><Input /></Form.Item></Col>
          <Col xs={24} sm={12}><Form.Item name="cms_company" label="CMS / Company"><Input /></Form.Item></Col>
        </Row>
        <Form.Item name="notes" label="Notes"><Input /></Form.Item>
      </Form>
    </Modal>
  );
}


function buildStatement(client, months, payments, selected) {
  const picked = months.filter((m) => selected.includes(m.month)).sort((a, b) => a.month.localeCompare(b.month));
  const rows = [];
  picked.forEach((m) => {
    m.channels.forEach((c) => rows.push([monthLabel(m.month), c.channel_name, c.channel_url, c.channel_id, c.revenue_usd, c.cut_percent, c.cut_usd, c.client_usd, m.usd_inr_rate ?? '', c.client_inr ?? '', c.partial ? 'Partial month (link/unlink date)' : '']));
    rows.push([`${monthLabel(m.month)} TOTAL`, '', '', '', m.revenue_usd, '', m.cut_usd, m.client_usd, m.usd_inr_rate ?? '', m.client_inr ?? '', m.settled ? `Settled ${m.settled_date}` : 'Pending']);
  });
  const inMonths = (p) => (p.month ? selected.includes(p.month) : selected.includes(String(p.pay_date || '').slice(0, 7)));
  const pays = payments.filter(inMonths);
  const sum = (arr, k) => Math.round(arr.reduce((s, x) => s + Number(x[k] || 0), 0) * 100) / 100;
  const totals = {
    revenue: sum(picked, 'revenue_usd'), cut: sum(picked, 'cut_usd'), share: sum(picked, 'client_usd'),
    inr: sum(picked, 'client_inr'), paidInr: sum(pays, 'amount_inr'), paidUsd: sum(pays, 'amount_usd'),
    unpriced: picked.some((m) => m.client_inr === null && m.client_usd > 0),
  };
  totals.balance = Math.round((totals.inr - totals.paidInr) * 100) / 100;
  return { picked, rows, pays, totals };
}

const shortMonth = (m) => new Date(`${m}-01T00:00:00`).toLocaleString('en-US', { month: 'short' }) + `-${m.slice(0, 4)}`;

async function downloadStatement(client, st) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  const thin = { style: 'thin' };
  const border = { top: thin, left: thin, bottom: thin, right: thin };
  const font = { name: 'Arial', size: 10 };
  const fmt = { 4: '"$"#,##0.00', 5: '0.00"%"', 6: '"$"#,##0.00', 7: '"₹"#,##0.00' };
  st.picked.forEach((m) => {
    const ws = wb.addWorksheet(shortMonth(m.month));
    ws.columns = [14, 28, 32, 20, 16, 20, 18].map((width) => ({ width }));
    const rate = m.usd_inr_rate ? Number(m.usd_inr_rate) : null;
    ws.mergeCells('A1:G1');
    ws.getCell('A1').value = `YOUTUBE CHANNEL Report for ${client.name}`;
    ws.getCell('A1').font = { ...font, size: 12, bold: true };
    ws.getCell('A1').alignment = { horizontal: 'left' };
    ws.mergeCells('A2:G2');
    ws.getCell('A2').value = `Report Month: ${shortMonth(m.month)} | Outgoing Rate: ${m.usd_inr_rate ?? '-'}`;
    ws.getCell('A2').font = font;
    const head = ws.addRow(['Activity Month', 'Channel ID', 'Channel Name', 'Topline Revenue ($)', 'Revenue Share (%)', 'Partner Payout ($)', 'Payout (INR)']);
    head.eachCell((c) => {
      c.font = { ...font, bold: true };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2F2F2' } };
      c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      c.border = border;
    });
    [...m.channels].sort((x, y) => y.revenue_usd - x.revenue_usd).forEach((c) => {
      const r = ws.addRow([m.month, c.channel_id, c.channel_url ? { text: c.channel_name, hyperlink: c.channel_url } : c.channel_name,
        c.revenue_usd, 100 - c.cut_percent, c.client_usd, rate ? Math.round(c.client_usd * rate * 100) / 100 : null]);
      r.eachCell({ includeEmpty: true }, (cell, i) => {
        cell.font = font;
        cell.border = border;
        if (fmt[i]) { cell.numFmt = fmt[i]; cell.alignment = { horizontal: 'right' }; }
      });
    });
    const t = ws.addRow(['TOTAL', '', '', m.revenue_usd, null, m.client_usd, rate ? Math.round(m.client_usd * rate * 100) / 100 : null]);
    ws.mergeCells(`A${t.number}:C${t.number}`);
    t.eachCell({ includeEmpty: true }, (cell, i) => {
      cell.font = { ...font, bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9F9F9' } };
      cell.border = border;
      if (fmt[i]) { cell.numFmt = fmt[i]; cell.alignment = { horizontal: 'right' }; }
    });
  });
  const buf = await wb.xlsx.writeBuffer();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  a.download = `${client.name}_Youtube Channel_${st.picked.map((m) => m.month).join('_')}.xlsx`.replace(/[\\/:*?"<>|]/g, '-');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

function ClientDetail({ clientId, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editClient, setEditClient] = useState(false);
  const [channelModal, setChannelModal] = useState({ open: false, channel: null });
  const [filter, setFilter] = useState('all');
  const [sheetMonths, setSheetMonths] = useState([]);
  const [payForm] = Form.useForm();
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/client-accounts/${clientId}`);
      setData(r.data);
      setSheetMonths((cur) => (cur.length ? cur : r.data.months?.[0] ? [r.data.months[0].month] : []));
    } catch (e) { message.error(errText(e)); } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const refreshCms = async () => {
    setRefreshing(true);
    try {
      await api.post('/client-accounts/refresh', {}, { timeout: 600000 });
      message.success('Latest revenue loaded from CMS');
      await load(); onChanged();
    } catch (e) { message.error(errText(e)); } finally { setRefreshing(false); }
  };
  const saveMonth = async (month, patch) => {
    try { await api.post(`/client-accounts/${clientId}/months/${month}`, patch); load(); onChanged(); } catch (e) { message.error(errText(e)); }
  };
  const addPayment = async () => {
    const v = await payForm.validateFields();
    try {
      await api.post(`/client-accounts/${clientId}/payments`, v);
      message.success('Payment added');
      payForm.resetFields();
      payForm.setFieldsValue({ pay_date: today(), kind: 'paid' });
      load(); onChanged();
    } catch (e) { message.error(errText(e)); }
  };
  const del = async (url, msg) => {
    try { await api.delete(url); message.success(msg); load(); onChanged(); return true; } catch (e) { message.error(errText(e)); return false; }
  };

  if (loading && !data) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
  if (!data) return <Empty />;
  const { client, channels, months, payments, summary } = data;
  const shownMonths = months.filter((m) => (filter === 'all' ? true : filter === 'settled' ? m.settled : !m.settled));
  const statement = buildStatement(client, months, payments, sheetMonths);

  const monthCols = [
    { title: 'Month', dataIndex: 'month', fixed: 'left', width: 140, render: (m, r) => <Space direction="vertical" size={0}><Text strong>{monthLabel(m)}</Text>{r.partial && <Tag color="orange">Partial month</Tag>}</Space> },
    { title: 'Revenue (100%)', dataIndex: 'revenue_usd', align: 'right', width: 120, render: usd },
    { title: 'Our cut', dataIndex: 'cut_usd', align: 'right', width: 110, render: usd },
    { title: 'Client share', dataIndex: 'client_usd', align: 'right', width: 120, render: (v) => <Text strong>{usd(v)}</Text> },
    { title: 'USD→INR rate', key: 'rate', width: 120, render: (_, r) => <InputNumber key={`${r.month}-${r.usd_inr_rate}`} size="small" min={0} step={0.01} defaultValue={r.usd_inr_rate ?? undefined} placeholder={summary.last_rate ? String(summary.last_rate) : 'Rate'} onBlur={(e) => { const v = e.target.value; if (String(v) !== String(r.usd_inr_rate ?? '')) saveMonth(r.month, { usd_inr_rate: v === '' ? '' : Number(v) }); }} style={{ width: 100 }} /> },
    { title: <Tooltip title="Optional: the actual INR received for this month (100%). Overrides rate × USD.">Total INR received</Tooltip>, key: 'ovr', width: 150, render: (_, r) => <InputNumber key={`${r.month}-${r.total_inr_override}`} size="small" min={0} defaultValue={r.total_inr_override ?? undefined} placeholder="Optional" onBlur={(e) => { const v = e.target.value; if (String(v) !== String(r.total_inr_override ?? '')) saveMonth(r.month, { total_inr_override: v === '' ? '' : Number(v) }); }} style={{ width: 130 }} /> },
    { title: <Tooltip title="Type the client's INR directly (e.g. 5000) — the rate is worked out from it. Or set the rate and this fills itself.">Client payable (INR)</Tooltip>, key: 'cinr', width: 150, render: (_, r) => <InputNumber key={`${r.month}-${r.client_inr}`} size="small" min={0} defaultValue={r.client_inr === null ? undefined : Math.round(r.client_inr)} placeholder="₹ or set rate" disabled={!r.client_usd} onBlur={(e) => { const v = Number(e.target.value); if (!e.target.value || !r.client_usd || Math.round(v) === Math.round(r.client_inr ?? -1)) return; saveMonth(r.month, { usd_inr_rate: Math.round((v / r.client_usd) * 10000) / 10000, total_inr_override: '' }); }} style={{ width: 130, fontWeight: 600 }} /> },
    { title: 'Settled', key: 'settled', width: 150, render: (_, r) => <Space direction="vertical" size={0}><Checkbox checked={r.settled} onChange={(e) => saveMonth(r.month, { settled: e.target.checked })}>{r.settled ? 'Done' : 'Pending'}</Checkbox>{r.settled && <Text type="secondary" style={{ fontSize: 12 }}>{r.settled_date}</Text>}</Space> },
  ];
  const channelBreakdownCols = [
    { title: 'Channel', dataIndex: 'channel_name', render: (v, r) => <Space>{v}{r.partial && <Tag color="orange">Partial</Tag>}</Space> },
    { title: 'Revenue', dataIndex: 'revenue_usd', align: 'right', render: usd },
    { title: 'Cut %', dataIndex: 'cut_percent', align: 'center', render: (v) => `${v}%` },
    { title: 'Our cut', dataIndex: 'cut_usd', align: 'right', render: usd },
    { title: 'Client share', dataIndex: 'client_usd', align: 'right', render: usd },
    { title: 'Client INR', dataIndex: 'client_inr', align: 'right', render: inr },
  ];

  return (
    <div>
      <PageHero icon={<TeamOutlined />} title={client.name}
        subtitle={`${summary.channel_count} channel(s)${summary.settled_till ? ` · Hisab settled till ${monthLabel(summary.settled_till)}` : ' · No month settled yet'}`}
        extra={<Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>All clients</Button>
          <Button icon={<SyncOutlined spin={refreshing} />} loading={refreshing} onClick={refreshCms}>Refresh from CMS</Button>
          <Button icon={<EditOutlined />} onClick={() => setEditClient(true)}>Edit</Button>
          <Popconfirm title="Delete this client? (Jis client ke channels ka revenue aa chuka hai, wo delete nahi hoga)" onConfirm={async () => { if (await del(`/client-accounts/${clientId}`, 'Client deleted')) onBack(); }}>
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>} />

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><StatTile tone="blue" icon={<YoutubeOutlined />} label="Total revenue (100%)" value={usd(summary.revenue_usd)} /></Col>
        <Col xs={12} md={6}><StatTile tone="violet" icon={<DollarOutlined />} label="Client share" value={usd(summary.client_usd)} hint={summary.client_inr ? `${inr(summary.client_inr)} payable` : null} /></Col>
        <Col xs={12} md={6}><StatTile tone="green" icon={<WalletOutlined />} label="Paid (incl. advance)" value={inr(summary.paid_inr)} hint={[summary.advance_inr ? `Advance diya ${inr(summary.advance_inr)}` : null, summary.paid_usd ? usd(summary.paid_usd) : null].filter(Boolean).join(' · ') || null} /></Col>
        {summary.advance_left_inr > 0
          ? <Col xs={12} md={6}><StatTile tone="amber" icon={<MoneyCollectOutlined />} label="Advance baaki (client par)" value={inr(summary.advance_left_inr)} hint="Aage ki kamai se katega" /></Col>
          : <Col xs={12} md={6}><StatTile tone={summary.balance_inr > 0 ? 'red' : 'green'} icon={<MoneyCollectOutlined />} label="Balance to pay" value={inr(summary.balance_inr)} hint={summary.unpriced_usd ? `+ ${usd(summary.unpriced_usd)} without INR rate` : null} /></Col>}
      </Row>
      {summary.advance_inr > 0 && (
        <Alert type={summary.advance_left_inr > 0 ? 'warning' : 'success'} showIcon style={{ marginBottom: 16 }}
          message={<span>Advance diya: <b>{inr(summary.advance_inr)}</b> · Kamai se adjust hua: <b>{inr(summary.advance_adjusted_inr)}</b> · Advance baaki: <b>{inr(summary.advance_left_inr)}</b></span>} />
      )}

      <Tabs items={[
        {
          key: 'hisab', label: 'Monthly Hisab',
          children: <Card size="small" title={<Segmented value={filter} onChange={setFilter} options={[{ label: 'All months', value: 'all' }, { label: 'Pending', value: 'pending' }, { label: 'Settled', value: 'settled' }]} />}>
            <Table rowKey="month" size="small" loading={loading} dataSource={shownMonths} columns={monthCols} pagination={false} scroll={{ x: 1050 }}
              expandable={{ expandedRowRender: (r) => <Table rowKey="channel_row_id" size="small" pagination={false} dataSource={r.channels} columns={channelBreakdownCols} scroll={{ x: 640 }} /> }}
              locale={{ emptyText: channels.length ? 'No revenue yet — click "Refresh from CMS" on the client list' : 'Add this client\'s channels first' }} />
          </Card>,
        },
        {
          key: 'channels', label: `Channels (${channels.length})`,
          children: <Card size="small" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setChannelModal({ open: true, channel: null })}>Add channel</Button>}>
            <Table rowKey="id" size="small" dataSource={channels} pagination={false} scroll={{ x: 900 }} columns={[
              { title: 'Channel', dataIndex: 'channel_name', render: (v, r) => <Space direction="vertical" size={0}><a href={r.channel_url} target="_blank" rel="noreferrer">{v || r.cms_name || r.channel_id}</a><Text type="secondary" style={{ fontSize: 12 }}>{r.channel_id}</Text></Space> },
              { title: 'Cut %', dataIndex: 'cut_percent', align: 'center', width: 80, render: (v) => `${v}%` },
              { title: 'Linked', dataIndex: 'linked_date', width: 110, render: (v) => v || '—' },
              { title: 'Unlinked', dataIndex: 'unlinked_date', width: 110, render: (v) => (v ? <Tag color="red">{v}</Tag> : <Tag color="green">Active</Tag>) },
              { title: 'Total revenue', dataIndex: 'total_usd', align: 'right', width: 120, render: usd },
              { title: 'Vendor', dataIndex: 'vendor', width: 120 },
              { title: '', key: 'a', width: 90, render: (_, r) => <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => setChannelModal({ open: true, channel: r })} />
                <Popconfirm title="Remove this channel? (Revenue aa chuka hai to delete nahi hoga, Unlink date daalo)" onConfirm={() => del(`/client-accounts/channels/${r.id}`, 'Channel removed')}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm>
              </Space> },
            ]} />
          </Card>,
        },
        {
          key: 'payments', label: `Payments (${payments.length})`,
          children: <Card size="small">
            <Form form={payForm} layout="inline" initialValues={{ pay_date: today(), kind: 'paid' }} style={{ rowGap: 8, marginBottom: 12 }}>
              <Form.Item name="pay_date" label="Date"><Input type="date" /></Form.Item>
              <Form.Item name="kind"><Select style={{ width: 120 }} options={[{ value: 'paid', label: 'Payment' }, { value: 'advance', label: 'Advance' }]} /></Form.Item>
              <Form.Item name="month"><Select allowClear placeholder="For month (optional)" style={{ width: 190 }} options={months.map((m) => ({ value: m.month, label: monthLabel(m.month) }))} /></Form.Item>
              <Form.Item name="amount_inr"><InputNumber min={0} placeholder="₹ INR" style={{ width: 130 }} /></Form.Item>
              <Form.Item name="amount_usd"><InputNumber min={0} placeholder="$ USD" style={{ width: 110 }} /></Form.Item>
              <Form.Item name="note"><Input placeholder="Note" style={{ width: 180 }} /></Form.Item>
              <Button type="primary" icon={<PlusOutlined />} onClick={addPayment}>Add</Button>
            </Form>
            <Table rowKey="id" size="small" dataSource={payments} pagination={{ pageSize: 20 }} scroll={{ x: 700 }} columns={[
              { title: 'Date', dataIndex: 'pay_date', width: 110 },
              { title: 'Type', dataIndex: 'kind', width: 100, render: (v) => <Tag color={v === 'advance' ? 'gold' : 'green'}>{v === 'advance' ? 'Advance' : 'Payment'}</Tag> },
              { title: 'For month', dataIndex: 'month', width: 140, render: (v) => (v ? monthLabel(v) : '—') },
              { title: 'INR', dataIndex: 'amount_inr', align: 'right', width: 120, render: (v) => (Number(v) ? inr(v) : '—') },
              { title: 'USD', dataIndex: 'amount_usd', align: 'right', width: 100, render: (v) => (Number(v) ? usd(v) : '—') },
              { title: 'Note', dataIndex: 'note' },
              { title: '', key: 'a', width: 50, render: (_, r) => <Popconfirm title="Delete this payment?" onConfirm={() => del(`/client-accounts/payments/${r.id}`, 'Payment deleted')}><Button size="small" danger icon={<DeleteOutlined />} /></Popconfirm> },
            ]} />
          </Card>,
        },
        {
          key: 'sheet', label: 'Download Report',
          children: <Card size="small"
            title={<Select mode="multiple" allowClear style={{ minWidth: 260, maxWidth: '100%' }} value={sheetMonths} onChange={setSheetMonths} placeholder="Choose month(s)"
              options={[...months].sort((x, y) => x.month.localeCompare(y.month)).map((m) => ({ value: m.month, label: monthLabel(m.month) }))} />}
            extra={<Space wrap>
              <Button icon={<SyncOutlined spin={refreshing} />} loading={refreshing} onClick={refreshCms}>Refresh from CMS</Button>
              <Button type="primary" icon={<DownloadOutlined />} disabled={!statement.picked.length || refreshing} onClick={() => downloadStatement(client, statement).catch((e) => message.error(e.message || 'Download failed'))}>Download Excel</Button>
            </Space>}>
            {statement.picked.length ? <>
              {statement.picked.map((m) => (
                <div key={m.month} style={{ marginBottom: 16 }}>
                  <Text strong>Report Month: {shortMonth(m.month)} | Outgoing Rate: {m.usd_inr_rate ?? '-'}</Text>
                  <Table rowKey="channel_row_id" size="small" pagination={false} scroll={{ x: 900 }} style={{ marginTop: 6 }}
                    dataSource={[...m.channels].sort((x, y) => y.revenue_usd - x.revenue_usd)}
                    columns={[
                      { title: 'Activity Month', render: () => m.month },
                      { title: 'Channel ID', dataIndex: 'channel_id' },
                      { title: 'Channel Name', dataIndex: 'channel_name', render: (v, c) => (c.channel_url ? <a href={c.channel_url} target="_blank" rel="noreferrer">{v}</a> : v) },
                      { title: 'Topline Revenue ($)', dataIndex: 'revenue_usd', align: 'right', render: usd },
                      { title: 'Revenue Share (%)', dataIndex: 'cut_percent', align: 'right', render: (v) => `${100 - v}%` },
                      { title: 'Partner Payout ($)', dataIndex: 'client_usd', align: 'right', render: usd },
                      { title: 'Payout (INR)', dataIndex: 'client_usd', align: 'right', render: (v) => (m.usd_inr_rate ? inr(v * m.usd_inr_rate) : '-') },
                    ]}
                    summary={() => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={3}><Text strong>TOTAL</Text></Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right"><Text strong>{usd(m.revenue_usd)}</Text></Table.Summary.Cell>
                        <Table.Summary.Cell index={4} />
                        <Table.Summary.Cell index={5} align="right"><Text strong>{usd(m.client_usd)}</Text></Table.Summary.Cell>
                        <Table.Summary.Cell index={6} align="right"><Text strong>{m.usd_inr_rate ? inr(m.client_usd * m.usd_inr_rate) : '-'}</Text></Table.Summary.Cell>
                      </Table.Summary.Row>
                    )} />
                </div>
              ))}
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                <Col xs={12} md={6}><StatTile tone="blue" icon={<DollarOutlined />} label="Client share (USD)" value={usd(statement.totals.share)} /></Col>
                <Col xs={12} md={6}><StatTile tone="violet" icon={<MoneyCollectOutlined />} label="Client share (INR)" value={inr(statement.totals.inr)} hint={statement.totals.unpriced ? 'Some months have no rate' : null} /></Col>
                <Col xs={12} md={6}><StatTile tone="green" icon={<WalletOutlined />} label="Paid in these months" value={inr(statement.totals.paidInr)} /></Col>
                <Col xs={12} md={6}><StatTile tone={statement.totals.balance > 0 ? 'red' : 'green'} icon={<MoneyCollectOutlined />} label="Balance" value={inr(statement.totals.balance)} /></Col>
              </Row>
            </> : <Empty description="Choose one or more months" />}
          </Card>,
        },
      ]} />

      <ClientForm open={editClient} client={client} onClose={() => setEditClient(false)} onSaved={() => { setEditClient(false); load(); onChanged(); }} />
      <ChannelForm open={channelModal.open} clientId={clientId} channel={channelModal.channel} defaultCut={Number(client.default_cut || 0)}
        onClose={() => setChannelModal({ open: false, channel: null })} onSaved={() => { setChannelModal({ open: false, channel: null }); load(); onChanged(); }} />
    </div>
  );
}

export default function ClientAccounts() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('client');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [newClient, setNewClient] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/client-accounts'); setClients(r.data.clients || []); } catch (e) { message.error(errText(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try { await api.post('/client-accounts/refresh', {}, { timeout: 600000 }); message.success('Revenue updated from CMS'); load(); } catch (e) { message.error(errText(e)); } finally { setRefreshing(false); }
  };
  const shown = useMemo(() => clients.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())), [clients, search]);
  const open = (id) => setParams(id ? { client: String(id) } : {});

  if (selected) return <ClientDetail clientId={selected} onBack={() => open(null)} onChanged={load} />;

  const totalBalance = clients.reduce((s, c) => s + Math.max(0, Number(c.balance_inr || 0)), 0);
  const totalAdvanceLeft = clients.reduce((s, c) => s + Number(c.advance_left_inr || 0), 0);
  return (
    <div>
      <PageHero icon={<MoneyCollectOutlined />} title="Client Channels" subtitle="Click a client to open its channels, monthly revenue, payments and hisab"
        extra={<Space wrap>
          <Button icon={<SyncOutlined spin={refreshing} />} loading={refreshing} onClick={refresh}>Refresh from CMS</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewClient(true)}>New client</Button>
        </Space>} />
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><StatTile tone="violet" icon={<TeamOutlined />} label="Clients" value={clients.length} /></Col>
        <Col xs={12} md={6}><StatTile tone="blue" icon={<YoutubeOutlined />} label="Channels" value={clients.reduce((s, c) => s + Number(c.channel_count || 0), 0)} /></Col>
        <Col xs={12} md={6}><StatTile tone={totalBalance > 0 ? 'red' : 'green'} icon={<WalletOutlined />} label="Total balance to pay" value={inr(totalBalance)} /></Col>
        <Col xs={12} md={6}><StatTile tone="amber" icon={<MoneyCollectOutlined />} label="Total advance baaki" value={inr(totalAdvanceLeft)} hint="Clients ke paas pada advance" /></Col>
      </Row>
      <Input.Search allowClear placeholder="Search client" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320, marginBottom: 12 }} />
      {loading ? <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div> : !shown.length ? <Empty description={clients.length ? 'No client matches' : 'No clients yet — click "New client"'} /> : (
        <Row gutter={[12, 12]}>
          {shown.map((c) => (
            <Col xs={24} sm={12} lg={8} key={c.id}>
              <Card hoverable onClick={() => open(c.id)} size="small" title={<Space><TeamOutlined /><Text strong>{c.name}</Text></Space>}
                extra={c.pending_months ? <Tag color="orange">{c.pending_months} pending</Tag> : <Tag color="green" icon={<CheckCircleOutlined />}>Settled</Tag>}>
                <Row gutter={8}>
                  <Col span={12}><Text type="secondary">Channels</Text><div><Text strong>{c.channel_count}</Text></div></Col>
                  <Col span={12}><Text type="secondary">Client share</Text><div><Text strong>{usd(c.client_usd)}</Text></div></Col>
                  {c.advance_left_inr > 0
                    ? <Col span={12} style={{ marginTop: 8 }}><Text type="secondary">Advance baaki</Text><div><Text strong style={{ color: '#d48806' }}>{inr(c.advance_left_inr)}</Text></div></Col>
                    : <Col span={12} style={{ marginTop: 8 }}><Text type="secondary">Balance</Text><div><Text strong type={c.balance_inr > 0 ? 'danger' : undefined}>{inr(c.balance_inr)}</Text></div></Col>}
                  <Col span={12} style={{ marginTop: 8 }}><Text type="secondary">Settled till</Text><div><Text>{c.settled_till ? monthLabel(c.settled_till) : '—'}</Text></div></Col>
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      )}
      <ClientForm open={newClient} onClose={() => setNewClient(false)} onSaved={() => { setNewClient(false); load(); }} />
    </div>
  );
}
